import PropTypes from "prop-types";
import { useRef, useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { useSbomVerificationRequest } from "../../hooks/useSbomVerificationRequest";
import { formatDateTime, downloadFile } from "../../utils/common";
import { formatSbomApiError } from "@utils/error";
import { UploadIcon } from "../Icons";
import TableWithPagination from "../Tables/TableWithPagination";
import { CHARACTER_LENGTH } from "@constants/index";
import {
  ClaritySC,
  SBOM_IMPORT_ACCEPT,
  SBOM_MANUAL_FORMAT_OPTIONS,
  formatSbomImportExtensionsLabelAll,
  isAllowedSbomImportFileAny,
  SBOM_DETECT_STATUS_OK,
  SBOM_DETECT_STATUS_UNSUPPORTED,
  SBOM_VERIFICATION_TYPE_CLARITY_SC,
} from "@constants/sbom";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomTextarea } from "@components/Forms";
import CustomSelect from "@components/UI/CustomSelect";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants";

const SbomVerificationRequest = ({ isOpen, onClose, onSubmit }) => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [detection, setDetection] = useState(null);
  const [detectState, setDetectState] = useState("idle"); // idle | loading | ok | error
  const [manualSbomType, setManualSbomType] = useState(null);

  const { listData, detectSbomFormat } = useSbomVerificationRequest(currentPage);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      verifFile: null,
      importMethod: "upload",
      sbomSeq: "",
      message: "",
      verificationType: "sbom",
    },
  });

  const clarityScData = useMemo(
    () => (listData || []).filter((sbom) => sbom.sbomTypeCd === ClaritySC),
    [listData],
  );
  const pagedClarityScData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return clarityScData.slice(start, start + itemsPerPage);
  }, [clarityScData, currentPage, itemsPerPage]);
  const totalPagesFromHook = Math.max(1, Math.ceil((clarityScData.length || 0) / itemsPerPage));

  const watchSelectedSbom = watch("sbomSeq");
  const hasSelectedSbom = watchSelectedSbom != null && watchSelectedSbom !== "";
  const detectionOk = detectState === "ok" && detection?.status === SBOM_DETECT_STATUS_OK;
  const detectUnsupported =
    detectState === "error" && detection?.status === SBOM_DETECT_STATUS_UNSUPPORTED;
  const detectFailed =
    detectState === "error" && detection?.status !== SBOM_DETECT_STATUS_UNSUPPORTED;

  const resolvedSbomType = detectionOk
    ? detection?.sbomType
    : detectFailed
      ? manualSbomType
      : null;
  const isClaritySc =
    resolvedSbomType === SBOM_VERIFICATION_TYPE_CLARITY_SC ||
    (detectionOk && detection?.requiresSbomSeq === true);

  const resetDetection = () => {
    setDetection(null);
    setDetectState("idle");
    setManualSbomType(null);
    setValue("sbomSeq", "");
    setFileError("");
  };

  const runDetect = async (file) => {
    setDetectState("loading");
    setDetection(null);
    setManualSbomType(null);
    setValue("sbomSeq", "");
    try {
      const result = await detectSbomFormat(file);
      setDetection(result);
      if (result?.status === SBOM_DETECT_STATUS_OK && result?.sbomType) {
        setDetectState("ok");
        setFileError("");
      } else if (result?.status === SBOM_DETECT_STATUS_UNSUPPORTED) {
        setDetectState("error");
        setFileError(
          t("project.verification-request.format-unsupported-version", {
            format: result?.displayName || "",
          }),
        );
      } else {
        setDetectState("error");
        const detail = formatSbomApiError(
          t,
          { messageKey: result?.messageKey, params: result?.params },
          i18n.language,
        );
        setFileError(detail || "");
      }
    } catch {
      setDetectState("error");
      setDetection(null);
      setFileError("");
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    if (!isAllowedSbomImportFileAny(file.name)) {
      setSelectedFile(null);
      resetDetection();
      setFileError(
        t("project.verification-request.sbom-invalid-file-type-error", {
          extensions: formatSbomImportExtensionsLabelAll(),
        }),
      );
      return;
    }
    setSelectedFile(file);
    setFileError("");
    await runDetect(file);
  };

  const handleBrowseFile = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadExcelTemplate = async () => {
    try {
      await downloadFile(
        "CSC_SBOM_Component_Import_Template.xlsx",
        `/${queryKeys.downloadSbomExcelTemplate}`,
      );
    } catch (error) {
      console.error("Error downloading Excel template:", error);
      ToastConstant(TOAST.error, t("toast.download.error.title"), t("toast.download.error.message"));
    }
  };

  const handleManualFormatChange = (option) => {
    setManualSbomType(option?.value || null);
    setValue("sbomSeq", "");
  };

  const handleSbomSelection = (selectedRows) => {
    if (selectedRows.length > 0) {
      setValue("sbomSeq", selectedRows[0]);
    }
  };

  const handleSbomRowClick = (row) => {
    handleSbomSelection([row.sbomSeq]);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleCancel = () => {
    reset();
    setSelectedFile(null);
    setCurrentPage(1);
    resetDetection();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const columnConfig = {
    sbomName: {
      headerRender: () => t("project.sbom.sbom-name"),
    },
    sbomTypeCd: {
      headerRender: () => t("project.sbom.sbom-type"),
    },
    createdAt: {
      headerRender: () => t("project.creation-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  const canSubmit =
    !!selectedFile &&
    detectState !== "loading" &&
    !detectUnsupported &&
    !!resolvedSbomType &&
    (!isClaritySc || hasSelectedSbom);

  const onSubmitForm = (data) => {
    const verifFileData = selectedFile ? { 0: selectedFile, length: 1 } : null;
    if (!verifFileData || !resolvedSbomType) return;
    if (isClaritySc && !hasSelectedSbom) return;

    const submitData = {
      ...data,
      verifFile: verifFileData,
      verificationType: "sbom",
      sbomType: resolvedSbomType,
      sbomSeq: isClaritySc ? data.sbomSeq : "",
      sbomDetect: detection,
    };

    if (onSubmit) {
      onSubmit(submitData);
    }

    handleCancel();
  };

  if (!isOpen) return null;

  const detectedLabel = detection?.displayName
    ? detection.serialization
      ? `${detection.displayName} (${detection.serialization})`
      : detection.displayName
    : "";

  const manualFormatValue =
    SBOM_MANUAL_FORMAT_OPTIONS.find((option) => option.value === manualSbomType) || null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-start md:justify-center ${colors.bgBlurModal} backdrop-blur-sm overflow-x-auto p-4`} data-testid="sbom-verification-request">
      <div className={`${colors.bgModal} flex max-h-[95vh] w-[896px] min-w-[896px] flex-col rounded-lg shadow-xl`}>
        <div className={`border-b px-6 py-4 ${colors.borderPrimary} flex-shrink-0`}>
          <h2 className={`${colors.textPrimary} text-xl font-bold`}>{t("sbom.sbom-import-request")}</h2>
          <p className={`${colors.textSecondary} mt-1 text-sm`}>
            {t("project.verification-request.description")}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)} data-testid="sbom-verification-request-form" className={`flex-1 space-y-6 overflow-y-auto px-6 py-6`}>
          <div>
            <h3 className={`${colors.textPrimary} mb-3 text-sm font-medium`}>{t("project.verification-request.upload-file-section")} <span className={colors.textRequired}>*</span></h3>
            <div className={`rounded-lg border p-4 transition-all duration-200 ${colors.borderPrimary}`}>
              <div className="flex flex-1 items-center justify-between">
                <span className={`${colors.textPrimary} text-sm font-medium`}>{t("project.verification-request.upload-file")}</span>
                <div className="flex items-center space-x-2">
                  {selectedFile && <span className={`${colors.textSecondary} text-sm max-w-[550px] truncate`}
                    title={selectedFile.name}
                  >{selectedFile.name}</span>}
                  <button
                    type="button"
                    onClick={handleBrowseFile}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium ${colors.textAccentCyan} transition-colors ${colors.hoverTextAccentCyan}`}
                  >
                    <UploadIcon size={20} className={colors.textAccentCyan} />
                    <span>{selectedFile ? t("project.verification-request.change-file") : t("project.verification-request.browse-file")}</span>
                  </button>
                </div>
              </div>
              {fileError && detectState !== "error" && (
                <p className={`mt-1 ${colors.textRequired} text-xs`}>{fileError}</p>
              )}
              <p className={`${colors.textSecondary} mt-2 text-xs`}>
                {t("project.verification-request.sbom-supported-file-types", {
                  extensions: formatSbomImportExtensionsLabelAll(),
                })}{" "}
                (
                <button
                  type="button"
                  onClick={handleDownloadExcelTemplate}
                  className={`text-xs font-normal underline ${colors.textAccentCyan} ${colors.hoverTextAccentCyan}`}
                  data-testid="sbom-download-excel-template"
                >
                  {t("project.verification-request.download-excel-template")}
                </button>
                )
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept={SBOM_IMPORT_ACCEPT}
            />
          </div>

          {selectedFile && (
            <div data-testid="sbom-detected-format-panel" className="space-y-4">
              <div>
                <h3 className={`${colors.textPrimary} mb-3 text-sm font-medium`}>
                  {t("project.verification-request.detected-format")}
                </h3>
                <div className={`rounded-lg border px-4 py-3 ${colors.borderPrimary}`}>
                  {detectState === "loading" && (
                    <p className={`${colors.textSecondary} text-sm`}>
                      {t("project.verification-request.detecting-format")}
                    </p>
                  )}
                  {detectionOk && (
                    <p className={`${colors.textPrimary} text-sm font-medium`}>{detectedLabel}</p>
                  )}
                  {detectUnsupported && (
                    <p className={`${colors.textRequired} text-sm`}>
                      {fileError || t("project.verification-request.format-unsupported-version", {
                        format: detection?.displayName || "",
                      })}
                    </p>
                  )}
                  {detectFailed && (
                    <div className={`${colors.textRequired} text-sm space-y-1`}>
                      <p>{t("project.verification-request.format-unrecognized")}</p>
                      {fileError ? <p>{fileError}</p> : null}
                    </div>
                  )}
                </div>
              </div>

              {detectFailed && (
                <div data-testid="sbom-manual-format-panel">
                  <h3 className={`${colors.textPrimary} mb-2 text-sm font-medium`}>
                    {t("project.verification-request.manual-format")}{" "}
                    <span className={colors.textRequired}>*</span>
                  </h3>
                  <p className={`${colors.textSecondary} mb-3 text-sm`}>
                    {t("project.verification-request.manual-format-hint")}
                  </p>
                  <CustomSelect
                    options={SBOM_MANUAL_FORMAT_OPTIONS}
                    value={manualFormatValue}
                    onChange={handleManualFormatChange}
                    placeholder={t("project.verification-request.manual-format-placeholder")}
                    clearable={false}
                    isSearchable={false}
                    usePortal
                    menuPlacement="auto"
                    data-testid="sbom-manual-format-select"
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label htmlFor="message" className={`${colors.textPrimary} mb-3 block text-sm font-medium`}>
              {t("project.verification-request.request-comment")}
            </label>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <>
                  <CustomTextarea
                    {...field}
                    placeholder={t("project.verification-request.write-comment")}
                    rows={3}
                    maxLength={CHARACTER_LENGTH.REQUEST}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="sbom-verification-request-message-char-count">
                    {(field.value || "").length}/{CHARACTER_LENGTH.REQUEST}
                  </p>
                </>
              )}
            />
          </div>

          {isClaritySc && (
            <div className="min-h-0 flex-1">
              <h3 className={`${colors.textPrimary} mb-3 text-sm font-medium`}>
                {t("project.verification-request.sbom-setting")}{" "}
                <span className={colors.textRequired}>*</span>
              </h3>
              <p className={`${colors.textSecondary} mb-4 text-sm`}>
                {t("project.verification-request.claritysc-setting-description")}
              </p>

              <div className="min-h-0 flex-1">
                <TableWithPagination
                  columns={["sbomName", "sbomTypeCd", "createdAt"]}
                  columnConfig={columnConfig}
                  data={pagedClarityScData}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalPages={totalPagesFromHook}
                  totalData={clarityScData.length}
                  loading={false}
                  hasRadioButton={true}
                  selectedRows={[watchSelectedSbom]}
                  onCheckboxChange={handleSbomSelection}
                  onRowClick={handleSbomRowClick}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  rowKey="sbomSeq"
                  maxRowsPerPage={itemsPerPage}
                  shouldShowGoToPage={true}
                  showPagination={true}
                />
              </div>
            </div>
          )}
        </form>

        <div
          className={`flex items-center justify-end space-x-3 border-t px-6 py-4 ${colors.borderPrimary} flex-shrink-0`}
        >
          <OutlineButton data-testid="sbom-verification-request-cancel-button" onClick={handleCancel}>{t("project.verification-request.cancel")}</OutlineButton>
          <PrimaryButton data-testid="sbom-verification-request-submit-button" onClick={handleSubmit(onSubmitForm)} disabled={!canSubmit}>
            {t("project.verification-request.request")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

SbomVerificationRequest.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};

export default SbomVerificationRequest;
